// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/FocusCarousel.tsx
// Version: v1.0.1
// Description: Focus Carousel mit Auto-Play und Navigation

import { ChevronLeft, ChevronRight } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Service-Item Interface
interface ServiceItem {
  id: number;
  imageUrl: string;
  title: string;
  subtitle: string;
  ctaHref: string;
}

// Props Interface mit erweiterten Optionen
export interface FocusCarouselProps<T = ServiceItem> {
  items?: T[];
  renderSlide?: (
    item: T,
    index: number,
    isActive: boolean,
    isHovered: boolean,
    shouldExpand: boolean
  ) => React.ReactNode;
  children?: React.ReactNode;
  autoplayMs?: number;
  initialIndex?: number;
  onSlideChange?: (index: number) => void;
  enableTouch?: boolean;
}

// ESTATA Service Items (als Fallback)
const defaultItems: ServiceItem[] = [
  {
    id: 1,
    imageUrl: "/images/EST-767664.jpg",
    title: "Generalplanung",
    subtitle:
      "Ganzheitliche Lösungen aus einer Hand, von der Konzeptentwicklung über die Planung bis zur schlüsselfertigen Umsetzung.",
    ctaHref: "/services/general-planning",
  },
  {
    id: 2,
    imageUrl: "/images/EST-505869746.jpg",
    title: "Construction Services",
    subtitle:
      "Komplette Bauausführung von der Grundsteinlegung bis zur Schlüsselübergabe mit höchsten Qualitätsstandards.",
    ctaHref: "/services/construction-services",
  },
  {
    id: 3,
    imageUrl: "/images/EST-978339282.jpg",
    title: "Due-Diligence Services",
    subtitle:
      "Professionelle Bewertung und Analyse von Immobilienprojekten für fundierte Investitionsentscheidungen.",
    ctaHref: "/services/due-diligence-services",
  },
  {
    id: 4,
    imageUrl: "/images/EST-1188930630.jpg",
    title: "Energieberatung",
    subtitle:
      "Nachhaltige Energiekonzepte für Neu- und Bestandsbauten mit maßgeschneiderten Lösungen.",
    ctaHref: "/services/energy-consulting",
  },
  {
    id: 5,
    imageUrl: "/images/EST-1304673834.jpg",
    title: "Projektsteuerung",
    subtitle:
      "Professionelle Koordination und Überwachung Ihrer Bauprojekte von der Planung bis zur Abnahme.",
    ctaHref: "/services/project-management",
  },
  {
    id: 6,
    imageUrl: "/images/EST-1338846217.jpg",
    title: "Development Strategies",
    subtitle:
      "Strategische Beratung für Immobilienentwicklungen mit Marktanalyse und Nutzungskonzepten.",
    ctaHref: "/services/development-strategies",
  },
  {
    id: 7,
    imageUrl: "/images/EST-1089040822.jpg",
    title: "Brandschutzplanung",
    subtitle:
      "Umfassende Brandschutzkonzepte für alle Gebäudearten mit normgerechten Plänen.",
    ctaHref: "/services/fire-protection",
  },
];

// Default Renderer für Service Items
const defaultRenderSlide = (
  item: ServiceItem,
  index: number,
  isActive: boolean,
  isHovered: boolean,
  shouldExpandProp: boolean
) => {
  const shouldExpand = shouldExpandProp;

  return (
    <>
      <div
        className="absolute top-0 right-0 w-px h-full bg-white/40 z-10"
        aria-hidden="true"
      />
      <div
        className={`absolute inset-0 transition-opacity duration-300 ${
          shouldExpand ? "bg-black/40" : "bg-black/20"
        }`}
      >
        <div
          className="absolute bottom-0 left-0 right-0 p-4 lg:p-6 xl:p-8"
          style={{ pointerEvents: "auto" }}
        >
          <div className="max-w-md">
            {/* Titel ohne Größenanimation */}
            <h3 className="text-white font-light mb-2 lg:mb-3 leading-tight text-xl lg:text-2xl xl:text-3xl">
              {item.title}
            </h3>

            <div
              className={`transition-all duration-300 overflow-hidden ${
                shouldExpand ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
              }`}
              aria-hidden={!shouldExpand}
            >
              <p className="text-white/80 text-sm lg:text-base mb-4 lg:mb-6 leading-relaxed">
                {item.subtitle}
              </p>
            </div>

            {/* Container mit fester Höhe für Button/Plus mit Sliding-Animation */}
            <div className="h-[28px] lg:h-[32px] flex items-center relative overflow-hidden">
              {/* Plus Symbol - slidet nach links raus */}
              <span
                className={`text-white text-2xl lg:text-3xl leading-none absolute transition-all duration-150 ease-out ${
                  shouldExpand
                    ? "transform -translate-x-8 opacity-0"
                    : "transform translate-x-0 opacity-80 group-hover:opacity-100"
                }`}
              >
                +
              </span>

              {/* Button - slidet von links rein */}
              <a
                href={item.ctaHref}
                className={`inline-flex items-center gap-2 text-white font-medium group/cta text-base lg:text-lg absolute transition-all duration-150 ease-out ${
                  shouldExpand
                    ? "transform translate-x-0 opacity-100"
                    : "transform -translate-x-8 opacity-0"
                }`}
                tabIndex={shouldExpand ? 0 : -1}
                style={{
                  cursor: "pointer",
                  pointerEvents: shouldExpand ? "auto" : "none",
                }}
              >
                Erfahre mehr
                <ChevronRight
                  size={16}
                  className="transition-transform duration-200 group-hover/cta:translate-x-1"
                  aria-hidden="true"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// SSR-safe useLayoutEffect
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export default function FocusCarousel<T = ServiceItem>(
  props: FocusCarouselProps<T>
) {
  const {
    items,
    renderSlide = defaultRenderSlide as any,
    children,
    autoplayMs = 6000,
    initialIndex = 0,
    onSlideChange,
    enableTouch = true,
  } = props;

  const currentItems = useMemo(() => {
    if (items && items.length > 0) return items;
    if (children) {
      const childArray = React.Children.toArray(children);
      if (childArray.length > 0) return childArray as T[];
    }
    return defaultItems as T[];
  }, [items, children]);

  const [spv, setSpv] = useState<number>(1);
  const [isClient, setIsClient] = useState(false);

  // Alle Refs zuerst definieren
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isHoveredRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number>();
  const touchStartXRef = useRef<number>(0);
  const touchDeltaXRef = useRef<number>(0);

  useIsomorphicLayoutEffect(() => {
    setIsClient(true);
    const calcSpv = () => {
      const w = window.innerWidth;
      if (w >= 1280) return 4;
      if (w >= 1024) return 3;
      if (w >= 640) return 2;
      return 1;
    };
    setSpv(calcSpv());
  }, []);

  // slideIndex so setzen, dass initialIndex im zweiten Feld (activePosition = 1) erscheint
  const [slideIndex, setSlideIndex] = useState<number>(spv + initialIndex + 2);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeItemIndex, setActiveItemIndex] = useState<number>(initialIndex);
  const [displayImageIndex, setDisplayImageIndex] =
    useState<number>(initialIndex);

  const [isInViewport, setIsInViewport] = useState(false);

  // Intersection Observer für Viewport-Detection
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isClient) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsInViewport(entry.isIntersecting);
      },
      { threshold: 0.3 } // 30% des Carousels muss sichtbar sein
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [isClient]);

  useEffect(() => {
    if (!isClient) return;

    const handleResize = () => {
      const w = window.innerWidth;
      let newSpv = 1;
      if (w >= 1280) newSpv = 4;
      else if (w >= 1024) newSpv = 3;
      else if (w >= 640) newSpv = 2;

      if (newSpv !== spv) {
        setSpv(newSpv);
        setSlideIndex(newSpv + activeItemIndex + 2);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isClient, spv, activeItemIndex]);

  const trackSlides = useMemo(() => {
    if (currentItems.length === 0) return [];
    const itemsArray = Array.isArray(currentItems)
      ? (currentItems as any[])
      : [currentItems];
    const head = itemsArray.slice(0, spv);
    const tail = itemsArray.slice(-spv);
    return [...tail, ...itemsArray, ...head];
  }, [currentItems, spv]);

  useEffect(() => {
    if (!isAnimating) {
      const activePosition = 1; // Zweites sichtbares Element ist aktiv
      const currentFirstVisibleIndex = slideIndex - spv;
      const activeIdx =
        (currentFirstVisibleIndex + activePosition) % currentItems.length;
      const normalizedActiveIdx =
        activeIdx < 0 ? activeIdx + currentItems.length : activeIdx;

      if (normalizedActiveIdx !== activeItemIndex) {
        setActiveItemIndex(normalizedActiveIdx);
        onSlideChange?.(normalizedActiveIdx);
      }
    }
  }, [
    slideIndex,
    spv,
    isAnimating,
    currentItems.length,
    activeItemIndex,
    onSlideChange,
  ]);

  const navigate = useCallback(
    (direction: "next" | "prev") => {
      if (isAnimating) return;
      setIsAnimating(true);

      // Hovering sofort beenden für saubere Animation
      setHoveredIndex(null);

      // Kurze Verzögerung vor dem Slide für saubere Text-Animation
      setTimeout(() => {
        setSlideIndex((prev) => {
          const newSlideIndex = direction === "next" ? prev + 1 : prev - 1;

          let nextActiveIdx;
          if (direction === "next") {
            nextActiveIdx = (activeItemIndex + 1) % currentItems.length;
          } else {
            nextActiveIdx =
              (activeItemIndex - 1 + currentItems.length) % currentItems.length;
          }

          // Hintergrundbild sofort setzen
          setDisplayImageIndex(nextActiveIdx);

          return newSlideIndex;
        });
      }, 50);
    },
    [isAnimating, activeItemIndex, currentItems.length]
  );

  const next = useCallback(() => navigate("next"), [navigate]);
  const prev = useCallback(() => navigate("prev"), [navigate]);

  const handleMouseEnter = useCallback(
    (index: number) => {
      if (!isAnimating) {
        setHoveredIndex(index);
        setDisplayImageIndex(index);
      }
    },
    [isAnimating]
  );

  const handleMouseLeave = useCallback(() => {
    if (!isAnimating) {
      setHoveredIndex(null);
      setDisplayImageIndex(activeItemIndex);
    }
  }, [isAnimating, activeItemIndex]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enableTouch || isAnimating) return;
      touchStartXRef.current = e.clientX;
      touchDeltaXRef.current = 0;
    },
    [enableTouch, isAnimating]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!enableTouch || touchStartXRef.current === 0) return;
      touchDeltaXRef.current = e.clientX - touchStartXRef.current;
    },
    [enableTouch]
  );

  const handlePointerUp = useCallback(() => {
    if (!enableTouch || touchStartXRef.current === 0) return;

    const threshold = 50;
    if (touchDeltaXRef.current > threshold) {
      prev();
    } else if (touchDeltaXRef.current < -threshold) {
      next();
    }

    touchStartXRef.current = 0;
    touchDeltaXRef.current = 0;
  }, [enableTouch, next, prev]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName !== "transform" || e.target !== track) return;

      setSlideIndex((curr) => {
        const newCurrentIndex =
          (((curr - spv) % currentItems.length) + currentItems.length) %
          currentItems.length;
        return newCurrentIndex + spv;
      });

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(() => {
        setIsAnimating(false);
      });
    };

    track.addEventListener("transitionend", handleTransitionEnd);
    return () => {
      track.removeEventListener("transitionend", handleTransitionEnd);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [currentItems.length, spv]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleContainerMouseEnter = () => {
      isHoveredRef.current = true;
    };
    const handleContainerMouseLeave = () => {
      isHoveredRef.current = false;
    };

    // Keyboard Navigation - nur wenn Carousel im Viewport ist
    const handleKeyDown = (e: KeyboardEvent) => {
      // Nur reagieren wenn das Carousel im Viewport ist
      if (!isInViewport) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      }
    };

    container.addEventListener("mouseenter", handleContainerMouseEnter, {
      passive: true,
    });
    container.addEventListener("mouseleave", handleContainerMouseLeave, {
      passive: true,
    });

    // Global keyboard listener
    document.addEventListener("keydown", handleKeyDown);

    const intervalId = setInterval(() => {
      if (!isHoveredRef.current && !isAnimating && hoveredIndex === null) {
        next();
      }
    }, autoplayMs);

    return () => {
      clearInterval(intervalId);
      container.removeEventListener("mouseenter", handleContainerMouseEnter);
      container.removeEventListener("mouseleave", handleContainerMouseLeave);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [autoplayMs, next, prev, isAnimating, hoveredIndex, isInViewport]);

  const slideBasis = 100 / spv;
  const translateX = -(slideIndex * slideBasis);

  if (!isClient) {
    return <div className="relative w-full h-[550px] bg-black" />;
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden h-[550px] bg-black group"
      role="region"
      aria-label="Services Carousel"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div className="absolute inset-0 z-0 pointer-events-none">
        {currentItems.map((item: any, index) => (
          <img
            key={index}
            src={item.imageUrl}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 pointer-events-none ${
              hoveredIndex === index ||
              (hoveredIndex === null && displayImageIndex === index)
                ? "opacity-100"
                : "opacity-0"
            }`}
            loading="lazy"
            decoding="async"
            aria-hidden="true"
          />
        ))}
      </div>

      <div
        ref={trackRef}
        className={`flex relative z-10 h-full ${
          isAnimating ? "transition-transform duration-700 ease-out" : ""
        }`}
        style={{
          transform: `translateX(${translateX}%)`,
          willChange: isAnimating ? "transform" : "auto",
        }}
      >
        {trackSlides.map((item, i) => {
          const visiblePosition = i - slideIndex;
          const isVisible = visiblePosition >= 0 && visiblePosition < spv;

          if (!isVisible) {
            return (
              <div
                key={i}
                style={{ flex: `0 0 ${slideBasis}%` }}
                className="relative h-full"
              >
                <div className="relative w-full h-full overflow-hidden bg-black/20" />
              </div>
            );
          }

          const itemIndex = i - spv;
          const normalizedItemIndex =
            ((itemIndex % currentItems.length) + currentItems.length) %
            currentItems.length;

          const isActive =
            normalizedItemIndex === activeItemIndex && !isAnimating;
          const isHovered = hoveredIndex === normalizedItemIndex;

          const shouldExpand =
            (isHovered || (isActive && hoveredIndex === null)) && !isAnimating;

          return (
            <div
              key={i}
              style={{ flex: `0 0 ${slideBasis}%` }}
              className="relative h-full"
            >
              <div
                className="relative w-full h-full overflow-hidden group"
                onMouseEnter={handleMouseEnter.bind(null, normalizedItemIndex)}
                onMouseLeave={handleMouseLeave}
              >
                {children
                  ? item
                  : renderSlide(
                      item as T,
                      normalizedItemIndex,
                      isActive,
                      isHovered,
                      shouldExpand
                    )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={prev}
        disabled={isAnimating}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 backdrop-blur-md rounded-full p-3 z-30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-white/50 opacity-0 group-hover:opacity-100"
        aria-label="Previous"
      >
        <ChevronLeft className="size-6 text-white" />
      </button>

      <button
        onClick={next}
        disabled={isAnimating}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 backdrop-blur-md rounded-full p-3 z-30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-white/50 opacity-0 group-hover:opacity-100"
        aria-label="Next"
      >
        <ChevronRight className="size-6 text-white" />
      </button>
    </div>
  );
}
